import { Mask, MaskCreateFunction, type EnvType } from './mask';
import { RequestQueue } from './request-queue';
import { AuthError } from './custom-catch';
import { PortalSelect, type PortalOptions } from './portal-select';
import { Singleton } from './utils/decorator';
import { inIframe } from './utils';

export interface Props {
  /**
   * ULP 域名
   */
  ulpOrigin: string;
  /**
   * 系统所在环境
   */
  env?: EnvType;
  /**
   * 是否显示遮罩
   */
  showMask?: boolean;
  /**
   * 业务系统自定义接口配置
   */
  authOptions?: {
    /**
     * 业务自定义鉴权接口地址
     */
    url: string;
    /**
     * ulp token要写入的domain
     */
    domain?: string;
    /**
     * cookieName
     */
    cookieName?: string;
  };
  /**
   * 登陆接口地址,会拼接到当前系统的url后面
   */
  loginPath?: string;
  /**
   * 自定义catch方法
   */
  customCatch?: (result: any) => any;
  /**
   * 登录方法;前端登陆时，sdk会调用该方法,判断是否登陆成功
   */
  loginMethod?: (token: string) => Promise<boolean>;
  /**
   * ulp未登陆时，加载ulp前
   */
  beforeVisitUlp?: (closeIframe: () => void) => void;
  /**
   * 入口配置
   */

  portalOptions?: Omit<PortalOptions, 'ulpOrigin'>;
}

export interface Request {
  // 请求状态
  code?: number;
}

type Awaited<T> = T extends Promise<infer U> ? U : T;

type WrapRequestType<T extends (...args: any[]) => any> = (
  ...args: Parameters<T>
) => Promise<Awaited<ReturnType<T>>>;

@Singleton
export class SSOSDK {
  // eslint-disable-next-line no-use-before-define
  private static instance: SSOSDK;

  private maskInstance!: Mask;

  // 是否开始登陆
  private isSSOPending = false;

  private requestQueue = new RequestQueue();

  private config!: Props;

  constructor(config: Props) {
    this.config = {
      customCatch: (error: any) => {
        console.error(error);
      },
      ...config,
    };

    const { customCatch } = this.config;

    this.maskInstance = new Mask({
      ...this.config,
      fontendLogin: !!this.config.loginMethod,
      successCallback: async token => {
        try {
          await this.reRequest(token);
        } catch (error) {
          customCatch?.(error);
        }
      },
    });
  }

  private handleExpiredRequest = <T extends (...args: any[]) => any>(
    requestFunction: T,
    args: Parameters<T>,
  ) => {
    const { customCatch } = this.config;
    return new Promise((resolve, reject) => {
      this.requestQueue.push({
        method: requestFunction,
        params: args,
        customCatch,
        resolve,
        reject,
      });

      if (!this.isSSOPending) {
        this.isSSOPending = true;
        this.maskInstance.startSSO();
      }
    });
  };

  setMaskCreateFn(fn: MaskCreateFunction): void {
    this.maskInstance.setMaskCreateFn(fn);
  }

  ulpLogout(callback?: () => void): void {
    this.maskInstance.ulpLogout(callback);
  }

  reRequest = async (token: string | null) => {
    if (!this.config.loginMethod) {
      this.requestQueue.executeStoredRequests();
    } else {
      const isLoginSuccess = await this.config.loginMethod(token!);
      if (isLoginSuccess) {
        this.requestQueue.executeStoredRequests();
      }
    }
    this.renderSelect();
    this.isSSOPending = false;
  };

  renderSelect = async () => {
    const { portalOptions } = this.config;
    if (!portalOptions) return;
    if (inIframe()) return;
    if (window.location.href.indexOf('sheinconsole') > -1) return;

    const Select = new PortalSelect({
      ...portalOptions,
      ulpOrigin: this.config.ulpOrigin,
    });
    Select.renderSelect();
  };

  wrapRequest =
    <T extends (...args: any[]) => any>(requestFunction: T): WrapRequestType<T> =>
    async (...args: Parameters<T>) => {
      try {
        const res = await requestFunction(...args);
        this.renderSelect();
        return res;
      } catch (error) {
        if (error instanceof AuthError || (error instanceof Error && error.message === '401')) {
          return this.handleExpiredRequest(requestFunction, args);
        }

        const { customCatch } = this.config;
        customCatch?.(error);
        throw error;
      }
    };
}
